import React, { useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { AppPage } from '../AppContent';
import { DirectoryItemId } from '../components/directory/DirectoryItemId';
import { useUsers } from '../hooks/DirectoryHooks';
import { useUserNetwork } from '../hooks/WPHooks';
import { useLogger } from '../services/LogService';
import { ModelsService } from '../services/ModelsService';
import { AppContext } from '../state/AppContextProvider';
import { WP_DirUser, WP_UserNetwork } from '../model/models';
import { IonIcon } from '@ionic/react';
import { FRIEND_ADD_ICON } from '../components/directory/DirectoryIcon';
import { Filter } from '../components/filters/Filter';
import { useFilterParents } from '../hooks/FilterHooks';
import { checkmark, optionsOutline } from 'ionicons/icons';

interface DirectoryPageProps extends RouteComponentProps {}

const DirectoryPage: React.FC<DirectoryPageProps> = (
  props: DirectoryPageProps
) => {
  const { state } = useContext(AppContext);

  const logger = useLogger('DirectoryPage');

  const parentTagsFiltered = state.parentTags.filter((tag) =>
    ['establishment', 'study_year', 'discipline'].includes(tag.slug)
  );

  const {
    userNetwork,
    create,
    update,
    remove,
    fetch,
    getNbFriendRequests,
    loading,
    friends,
  } = useUserNetwork(state.user ? state.user.id : null);

  const onChange = () => {
    logger.info('Applying filters');
  };

  const onResetFilter = () => {
    logger.info('Reseting filters');
    onReset();
  };

  const {
    selectParentTag,
    setSelectedParentTag,
    selectedParentTagId,
    childrenTags,
    selectSubTag,
    isSubTagSelected,
    selectedSubTags,
    setAllSelected,
    isFiltered,
    onReset,
    showModal,
    openModal,
  } = useFilterParents(parentTagsFiltered, onChange, false);

  const isFilter = isFiltered();

  const { refreshUser } = useUsers(0);

  const getFriendNetworkWithFilter = () => {
    const networkAndFriendsId = ModelsService.getMyFriends(
      userNetwork,
      state.user ? state.user.id : 0
    );

    const networkAndFriends = networkAndFriendsId.map((friendNetwork) => {
      let ret: { friend: null | WP_DirUser; network: WP_UserNetwork } = {
        friend: null,
        network: friendNetwork.network,
      };

      const friend = friends.find((ami) => ami.id === friendNetwork.friendId);

      if (friend) {
        ret.friend = friend;
      }

      return ret;
    });

    // We check if the tag ID is not already in this array, making sure we don't have duplicate tags
    const filtersId: number[] = [];
    selectedSubTags.forEach((tags) => {
      tags.tags.forEach((tag) => {
        if (filtersId.indexOf(tag.id) === -1) {
          filtersId.push(tag.id);
        }
      });
    });

    const filteredListUsers = networkAndFriends.filter((user) => {
      // If user has no friend
      if (!user.friend) {
        return false;
      }

      // If no filter tags selected
      if (filtersId.length === 0) {
        return true;
      }

      // If user has no tags
      if (!user.friend.acf.tags) {
        return false;
      }

      const userTagsListIds = user.friend.acf.tags.map((tag) => tag.term_id);

      // Check if user has all the tags
      const multipleExist = filtersId.every((value) => {
        return userTagsListIds?.includes(value);
      });

      return multipleExist;
    });

    const cloneUsers = filteredListUsers.map((user) => {
      return JSON.parse(JSON.stringify(user));
    });

    return cloneUsers;
  };

  const friendsNetwork = getFriendNetworkWithFilter();

  const nbFriendRequest = getNbFriendRequests();

  useEffect(() => {
    if (state.isConnected === true) {
      logger.info('useEffect() [] fetching friend req...');
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isConnected]);

  return (
    <>
      <div className="md:ml-64 md:py-10 md:overflow-x-hidden xl:mr-96 not-landing fixed bottom-0 right-0 z-[102]">
        <Link
          className="bg-primary cursor-pointer rounded-full w-12 h-12 shadow-xl flex justify-center items-center mr-4 md:mr-14 mb-6 md:mb-12"
          to="/search/users/s="
        >
          <IonIcon className="text-white text-lg" icon={FRIEND_ADD_ICON} />
        </Link>
      </div>
      <AppPage scrollable={true}>
        {nbFriendRequest > 0 && (
          <div className="inline md:hidden w-full">
            <Link
              to="/friend-request"
              className="w-full flex justify-center items-center bg-lightGreen text-primary font-bold text-sm py-3"
            >
              + {nbFriendRequest}{' '}
              <FormattedMessage
                id={
                  nbFriendRequest === 1
                    ? 'Contact request mobile'
                    : 'Contact requests mobile'
                }
              />
            </Link>
          </div>
        )}
        <div className="relative px-4 md:px-14">
          <div className="flex justify-between items-center md:justify-center">
            <div className="hidden md:block"></div>
            <h1 className="text-center text-2xl font-medium mb-4 mt-4">
              <FormattedMessage id="Directory" />
            </h1>
            <div className="md:absolute md:right-14">
              <Filter
                className="directory"
                multipleSelect={false}
                categories={parentTagsFiltered}
                selectParentTag={selectParentTag}
                setSelectedParentTag={setSelectedParentTag}
                selectedParentTagId={selectedParentTagId}
                childrenTags={childrenTags}
                selectSubTag={selectSubTag}
                isSubTagSelected={isSubTagSelected}
                selectedSubTags={selectedSubTags}
                setAllSelected={setAllSelected}
                onResetFilter={onResetFilter}
                isFiltered={isFiltered}
                backUrl={'/directory'}
                showModal={showModal}
                openModal={openModal}
              />
              <button onClick={() => openModal(true)}>
                <div className="bg-primary rounded-3xl px-2 py-2 flex justify-center items-center">
                  <IonIcon
                    icon={optionsOutline}
                    className="text-white  transform -rotate-90"
                  />
                  <p className="text-xs text-white uppercase font-bold pl-1">
                    <FormattedMessage id="Filter" />
                  </p>
                  {isFilter && (
                    <div className="w-5 h-5 rounded-full bg-black flex justify-center items-center ml-2">
                      <IonIcon
                        icon={checkmark}
                        className="text-lg text-white w-3 h-3"
                      />
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>
          {nbFriendRequest > 0 && (
            <div className="hidden md:block">
              <Link
                to="/friend-request"
                className="md:mb-6 font-bold rounded-full bg-lightGreen text-primary text-center mx-auto block w-64 px-1"
              >
                + {nbFriendRequest}
                <FormattedMessage
                  id={
                    nbFriendRequest === 1
                      ? 'Contact request'
                      : 'Contact requests'
                  }
                />
              </Link>
            </div>
          )}
          {loading && (
            <div className="">
              <FormattedMessage id="Loading" />
            </div>
          )}
          {isFilter && friendsNetwork.length === 0 && (
            <p className="text-center mt-10 text-error">
              <FormattedMessage id="No user filtered" />
            </p>
          )}
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 md:gap-4 min-h-auto">
            {userNetwork &&
              friendsNetwork.map((it, key) => (
                <DirectoryItemId
                  key={key}
                  user={it.friend}
                  network={it.network}
                  backUrl={'/directory'}
                  create={create}
                  update={update}
                  remove={remove}
                  refreshUser={refreshUser}
                />
              ))}
          </ul>
        </div>
      </AppPage>
    </>
  );
};

export default DirectoryPage;
